import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { fetchConfig, onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {boolean} */
  #pendingStickyAdd = false;

  /** @type {boolean} */
  #queuedStandardAdd = false;

  /** @type {number | undefined} */
  #pendingStickyAddTimeout;

  /** @type {string} */
  #currentVariantId = '';

  connectedCallback() {
    super.connectedCallback();

    if (this.#alwaysVisible) {
      this.#targetAddToCartButton = this.#resolveTargetAddToCartButton();
      this.#showStickyBar();
    } else {
      this.#setupIntersectionObserver();
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.refs.stickyBar.addEventListener('click', this.#handleStickySizeSwatchClick, { signal });

    this.#currentVariantId = this.dataset.currentVariantId || '';
    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#pendingStickyAddTimeout) {
      clearTimeout(this.#pendingStickyAddTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        // Only show sticky bar if buy buttons have been scrolled past (above viewport)
        if (!entry.isIntersecting && !this.#isStuck) {
          // Check if the element is above the viewport (scrolled past) or below (not yet reached)
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            // Element is above viewport - show sticky bar
            this.#showStickyBar();
          }
          // If rect.top >= 0, element is below viewport - don't show sticky bar yet
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      },
      {
        threshold: 0.25,
      }
    );

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = this.#resolveTargetAddToCartButton();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#isPersonalizationMode) {
      this.#openPersonalizationModal();
      return;
    }

    if (this.#isStandardMode) {
      if (this.#variantChangeInProgress) {
        this.#queuedStandardAdd = true;
        return;
      }

      await this.#addResolvedVariantToCart();
      return;
    }

    const targetButton = this.#resolveTargetAddToCartButton();
    if (!targetButton) return;
    if (this.#variantChangeInProgress) return;
    if (this.refs.addToCartButton.disabled || targetButton.disabled || this.dataset.variantAvailable !== 'true') return;

    targetButton.dataset.puppet = 'true';
    this.#pendingStickyAdd = true;
    if (this.#pendingStickyAddTimeout) clearTimeout(this.#pendingStickyAddTimeout);
    this.#pendingStickyAddTimeout = window.setTimeout(() => {
      this.#pendingStickyAdd = false;
      targetButton.dataset.puppet = 'false';
    }, 10000);
    targetButton.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;
    this.#variantChangeInProgress = false;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', this.#alwaysVisible ? 'true' : currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.#setCurrentVariantId(variant.id);
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = this.#resolveTargetAddToCartButton();
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    if (this.#alwaysVisible) {
      this.#showStickyBar();
    }

    // Restore the current quantity display if needed
    this.#updateButtonText();

    if (this.#isStandardMode && this.#queuedStandardAdd) {
      this.#queuedStandardAdd = false;
      window.setTimeout(() => {
        this.#addResolvedVariantToCart();
      }, 0);
    }
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = () => {
    this.#variantChangeInProgress = true;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.#setCurrentVariantId('');
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    const primaryTitleElement = this.querySelector('.sticky-add-to-cart__title');
    if (this.#isStandardMode && primaryTitleElement) {
      primaryTitleElement.textContent = selectedOptions;
    }
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag after cart operation
    if (!this.#isPersonalizationMode && this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (!this.#pendingStickyAdd) return;

    const eventProductId = event.detail?.data?.productId;
    if (eventProductId && eventProductId !== this.dataset.productId) return;

    this.#pendingStickyAdd = false;
    if (this.#pendingStickyAddTimeout) clearTimeout(this.#pendingStickyAddTimeout);
    if (event.type === ThemeEvents.cartError || event.detail?.data?.didError) return;
    if (event.detail?.data?.source === 'sticky-add-to-cart') return;

    this.#playAddToCartAnimation();
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (this.#isPersonalizationMode) return;

    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Handles size swatch click in the personalized sticky bar and syncs with the main variant picker.
   * @param {MouseEvent} event
   */
  #handleStickySizeSwatchClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatchButton = event.target.closest('[data-sticky-option-value-id]');
    if (!(swatchButton instanceof HTMLButtonElement)) return;

    const optionValueId = swatchButton.dataset.stickyOptionValueId;
    if (!optionValueId) return;

    const stickySizeValue = swatchButton.dataset.stickySizeValue || '';
    const optionPosition = swatchButton.dataset.stickyOptionPosition || '';
    this.#selectVariantFromStickySwatch(optionValueId, stickySizeValue, optionPosition);
  };

  /**
   * Selects an option in the main variant picker from sticky swatch interaction.
   * @param {string} optionValueId
   * @param {string} stickySizeValue
   * @param {string} optionPosition
   */
  #selectVariantFromStickySwatch(optionValueId, stickySizeValue, optionPosition) {
    const productId = this.dataset.productId;
    if (!productId) return;

    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantPicker) return;

    const targetInput = variantPicker.querySelector(`input[data-option-value-id="${optionValueId}"]`);

    if (!(targetInput instanceof HTMLInputElement)) return;
    if (targetInput.checked === false) {
      targetInput.click();
    }

    this.#setStickySwatchState(optionValueId, stickySizeValue, optionPosition);
  }

  async #addResolvedVariantToCart() {
    if (this.#pendingStickyAdd) return;
    if (this.#variantChangeInProgress) return;
    if (this.refs.addToCartButton.disabled || this.dataset.variantAvailable !== 'true') return;

    const variantId = this.#getResolvedVariantId();
    if (!variantId) return;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', this.#currentQuantity.toString());

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId || '')
      .filter(Boolean);
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    this.#pendingStickyAdd = true;
    if (this.#pendingStickyAddTimeout) clearTimeout(this.#pendingStickyAddTimeout);
    this.#pendingStickyAddTimeout = window.setTimeout(() => {
      this.#pendingStickyAdd = false;
    }, 10000);

    this.#playAddToCartAnimation();

    // The pup's room picks (dm-room.js) join this request, so they succeed or fail together.
    const room = /** @type {any} */ (document.querySelector('dm-room'));
    const extras = (room?.dataset.productId === this.dataset.productId ? room.getItems?.() ?? [] : []).map(
      (/** @type {any} */ { _title, _cardId, ...item }) => item
    );
    const plain = fetchConfig('javascript', { body: formData });
    const request = extras.length
      ? {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: this.#currentQuantity }, ...extras],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        }
      : { ...plain, headers: { ...plain.headers, Accept: 'text/html' } };

    try {
      const response = await fetch(Theme.routes.cart_add_url, request);
      const payload = await response.json();

      if (payload.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, payload.message, payload.description, payload.errors));
        return;
      }

      // The cart icon SETS its bubble to itemCount, so a multi-line add reports the cart's total.
      const itemCount = extras.length
        ? (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json())).item_count
        : this.#currentQuantity;

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'sticky-add-to-cart',
          itemCount,
          productId: this.dataset.productId,
          sections: payload.sections,
          variantId,
        })
      );
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @returns {string}
   */
  #getResolvedVariantId() {
    const variantFromOptions = this.#getVariantIdFromSelectedOptions();
    if (variantFromOptions) {
      this.#setCurrentVariantId(variantFromOptions);
      return variantFromOptions;
    }

    const productFormVariantId = this.#getProductFormVariantId();
    if (productFormVariantId) {
      this.#setCurrentVariantId(productFormVariantId);
      return productFormVariantId;
    }

    return this.#currentVariantId || this.dataset.currentVariantId || '';
  }

  /**
   * @returns {string}
   */
  #getVariantIdFromSelectedOptions() {
    const productId = this.dataset.productId;
    if (!productId) return '';

    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantPicker) return '';

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll('select option[selected], fieldset input:checked')
    )
      .map((option) => {
        if (option instanceof HTMLOptionElement) return option.value;
        if (option instanceof HTMLInputElement) return option.value;
        return '';
      })
      .filter(Boolean);
    if (selectedOptions.length === 0) return '';

    const variantsJson = variantPicker.querySelector('[data-variant-options-json]')?.textContent;
    if (!variantsJson) return '';

    try {
      const variants = JSON.parse(variantsJson);
      const matchingVariant = variants.find((variant) => {
        if (!Array.isArray(variant?.options)) return false;
        if (variant.options.length !== selectedOptions.length) return false;
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });

      if (!matchingVariant?.available || !matchingVariant?.id) return '';
      return matchingVariant.id.toString();
    } catch (error) {
      console.error(error);
      return '';
    }
  }

  /**
   * Updates sticky swatch visual selection immediately, before server morph returns.
   * @param {string} optionValueId
   * @param {string} stickySizeValue
   * @param {string} optionPosition
   */
  #setStickySwatchState(optionValueId, stickySizeValue, optionPosition) {
    const selector = optionPosition
      ? `[data-sticky-option-position="${optionPosition}"]`
      : '[data-sticky-option-value-id]';
    const swatches = Array.from(this.querySelectorAll(selector));
    swatches.forEach((swatch) => {
      if (!(swatch instanceof HTMLElement)) return;
      const isSelected = swatch.dataset.stickyOptionValueId === optionValueId;
      swatch.classList.toggle('is-selected', isSelected);
      swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (!stickySizeValue) return;
    const stickySizeValueElement = this.querySelector('[ref="stickySizeValue"]');
    if (stickySizeValueElement instanceof HTMLElement) {
      stickySizeValueElement.textContent = stickySizeValue;
    }
  }

  async #playAddToCartAnimation() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const addToCartButton = this.refs.addToCartButton;
    const productImage = this.refs.productImage || this.querySelector('[ref="productImage"]');

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    if (!cartIcon || !(productImage instanceof HTMLImageElement)) {
      this.#resetTimeout = setTimeout(() => {
        addToCartButton.removeAttribute('data-added');
      }, 800);
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 800);
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    if (this.#alwaysVisible) return;
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #resolveTargetAddToCartButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return null;

    const targetButton = productForm.querySelector('[ref="addToCartButton"]');
    if (!(targetButton instanceof HTMLButtonElement)) return null;

    this.#targetAddToCartButton = targetButton;
    return targetButton;
  }

  /**
   * @returns {string}
   */
  #getProductFormVariantId() {
    const productForm = this.#getProductForm();
    const variantInput = productForm?.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return '';
    return variantInput.value || '';
  }

  /**
   * Mobile personalized PDPs rely on the sticky CTA, so resolve the launcher lazily
   * and fall back to the personalization host if the main button is not yet hydrated.
   */
  #openPersonalizationModal() {
    const launcher = this.#resolveTargetAddToCartButton();
    if (launcher) {
      launcher.click();
      return;
    }

    const sectionElement = this.closest('.shopify-section');
    const sectionId = sectionElement?.id.replace('shopify-section-', '');
    if (!sectionId) return;

    const hostId = `WroePersonalization-${sectionId}`;
    const openHost = () => {
      const host = document.getElementById(hostId);
      if (host && typeof host.handleLauncherClick === 'function') {
        host.handleLauncherClick(new Event('click'));
      }
    };

    openHost();
    customElements.whenDefined('wroe-personalization-component').then(openHost);
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * @param {string|number} variantId
   */
  #setCurrentVariantId(variantId) {
    this.#currentVariantId = variantId.toString();
    this.dataset.currentVariantId = this.#currentVariantId;
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    if (this.#isPersonalizationMode || this.#isStandardMode) {
      quantityDisplay.style.display = 'none';
      return;
    }

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  get #isPersonalizationMode() {
    return this.dataset.personalizationMode === 'true';
  }

  get #isStandardMode() {
    return this.dataset.standardMode === 'true';
  }

  get #alwaysVisible() {
    return this.dataset.alwaysVisible === 'true';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
